import type { ReactNode } from "react";
import { publicPricingStyles } from "./publicEntryStyles";

export const pricingCell = "break-words px-2 py-4 align-middle sm:px-3 lg:px-6";
export const pricingName =
  "text-base font-semibold leading-6 text-[var(--landing-neutral-900)] sm:text-xl";
export const pricingAmount =
  "text-lg font-semibold leading-7 tabular-nums text-[var(--landing-neutral-900)] sm:text-[28px]";
export const pricingBody =
  "border-b border-slate-100 odd:bg-white even:bg-slate-50 last:border-b-0";

/** Shared presentation for the public offer types; purchasing rules stay in their existing models. */
export function PublicPricingTable({
  headers,
  children,
  label,
  eventDates = false,
}: {
  headers: [string, string, string];
  children: ReactNode;
  label: string;
  eventDates?: boolean;
}) {
  return (
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      className="overflow-x-auto rounded-xl border border-slate-200/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--landing-primary-600)]"
    >
      <table
        aria-label={label}
        className="w-full min-w-[380px] table-fixed text-left text-[14px] leading-6 text-slate-700 sm:text-base"
      >
        <colgroup>
          <col className="w-[28%] sm:w-[26%] lg:w-[23%] xl:w-[20%]" />
          <col className={eventDates ? "w-[44%] lg:w-[37%] xl:w-[40%]" : "w-[44%] lg:w-[57%] xl:w-[60%]"} />
          <col className={eventDates ? "w-[28%] sm:w-[30%] lg:w-[40%]" : "w-[28%] sm:w-[30%] lg:w-[20%]"} />
        </colgroup>
        <thead className={publicPricingStyles.tableHeader}>
          <tr>
            {headers.map((header, index) => (
              <th
                key={index}
                scope="col"
                className={`px-2 py-3 text-sm font-medium sm:px-3 lg:px-6 ${index === headers.length - 1 ? "text-right" : ""}`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        {children}
      </table>
    </div>
  );
}

/** Secondary pricing terms share a quiet separator across the public price list. */
export function PricingConditions({
  children,
  fullWidth = false,
}: {
  children: ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <div className="mt-3 space-y-1">
      <div
        aria-hidden="true"
        className={
          fullWidth
            ? "mb-3 h-px w-full bg-slate-900/10"
            : "mb-2 h-px w-10 bg-slate-200/70"
        }
      />
      {children}
    </div>
  );
}
